import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Award } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLang } from '@/i18n/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/products', label: t('nav_products') },
    { path: '/services', label: t('nav_services') },
    { path: '/about', label: t('nav_about') },
    { path: '/contact', label: t('nav_contact') },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* ISO Certification Bar */}
      <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-yellow-500 text-white py-1.5 sm:py-2 text-[11px] sm:text-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-center sm:justify-between gap-2">
          <div className="flex items-center gap-1.5 font-semibold tracking-wide" data-testid="iso-badge">
            <Award size={14} className="text-white" />
            <span>{t('iso_certified')}</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-white/90 text-xs uppercase tracking-widest">
            <span>{t('manufacturing_since')}</span>
          </div>
        </div>
      </div>

      {/* Top Bar — hidden on small screens to save vertical space */}
      <div className="hidden sm:block bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-gray-300 py-2 text-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">📍</span>
            <span className="font-medium">{t('factory_label')}</span>
          </div>
          <div className="flex gap-6 items-center">
            <a href="tel:+916280452690" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Phone size={14} />
              <span>+91 62804 52690</span>
            </a>
            <a href="tel:+916239019768" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Phone size={14} />
              <span>+91 62390 19768</span>
            </a>
            <a href="mailto:rkkohli.sons@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Mail size={14} />
              <span>rkkohli.sons@gmail.com</span>
            </a>
            <LanguageSwitcher compact />
          </div>
        </div>
      </div>

      {/* Main Header — white at top, transparent when scrolled */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/60 backdrop-blur-xl shadow-sm'
            : 'bg-white shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 sm:py-3">
            {/* Logo — full brand image inside rounded golden-outlined frame + wordmark beside */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group" data-testid="header-logo-link">
              <div className="h-10 sm:h-14 lg:h-16 flex-shrink-0 rounded-lg sm:rounded-xl border-2 border-yellow-400/80 ring-1 ring-yellow-300/30 shadow-[0_6px_18px_-6px_rgba(250,204,21,0.35)] overflow-hidden transition-transform duration-300 group-hover:scale-105">
                <img
                  src="/arkotherm-logo.jpeg"
                  alt="Arkotherm Furnaces — ISO 9001 Certified"
                  className="h-full w-auto object-contain"
                  data-testid="header-logo-img"
                />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-base sm:text-xl lg:text-2xl tracking-wide text-gray-900 font-semibold">
                  ARKOTHERM
                </span>
                <span className="text-[9px] sm:text-xs text-orange-600 mt-0.5 sm:mt-1 font-medium">
                  Industrial Furnaces
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 font-semibold text-sm transition-all duration-300 rounded-lg ${
                    isActive(link.path)
                      ? 'text-orange-600'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  data-testid={`nav-${link.path === '/' ? 'home' : link.path.replace('/', '')}`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full"></span>
                  )}
                </Link>
              ))}
              <LanguageSwitcher />
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="relative px-6 py-3 gradient-bg text-white font-bold rounded-full overflow-hidden group shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="header-request-quote-btn"
              >
                <span className="relative z-10">{t('request_quote')}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2.5 text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-300"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 font-semibold rounded-lg transition-all duration-300 ${
                    isActive(link.path)
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 px-4">
                <LanguageSwitcher />
              </div>
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-3.5 gradient-bg text-white font-bold rounded-full text-center mt-4 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t('request_quote')}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
